import _ from 'underscore';
import { Order } from '../../ts/helpers/Order';
import { ServerFilterClauses } from '../../ts/helpers/ServerFilterClauses';
import { ContentFilterKeyName, IContentFilters } from '../../ts/models/ContentFilters';
import { IContentType } from '../../ts/models/ContentTypes';
import { IListAndFieldValuesResource } from '../../ts/resources/Resource';
import { IResourceQuery } from '../../ts/resources/ResourceQuery';
import { IFilterClause, ILeafFilterClause } from '../../ts/ServerApi/types/IFilterClause';
import { IOrderClause } from '../../ts/ServerApi/types/IOrderClause';
import { WindowLocationService } from '~commonInfra/services/WindowLocationService';

export interface ISortableParams {
  order?: string;
}

class QueryService {

  public createQueryUpdater<T>(
      type: IContentType,
      resource: IListAndFieldValuesResource<T>,
      refreshFn: (resourceQuery: IResourceQuery<T>) => any,
      defaultFilters: Partial<IContentFilters<string>>
  ): IQueryUpdater {

    return new QueryUpdater<T>(
      type,
      resource,
      refreshFn,
      defaultFilters);
  }
}

export interface IQueryUpdater {
  refresh(): IResourceQuery<any>;
}

class QueryUpdater<T> implements IQueryUpdater {

  constructor(
    private type: IContentType,
    private resource: IListAndFieldValuesResource<T>,
    private refreshFn: (resourceQuery: IResourceQuery<T>) => any,
    private defaultFilters: Partial<IContentFilters<string>>,
    private filterKeys: ContentFilterKeyName[] = type.filterKeys
  ) {
  }

  // public methods

  public refresh() {
    const orderAndFilters = this.getCurrentOrderAndFilters();
    const order = orderAndFilters.order;
    const filters = orderAndFilters.filters;
    const extraQueryParams = this.type.extraQueryParams;
    const resourceQuery = this.createResourceQuery(order, filters, extraQueryParams);
    this.refreshFn(resourceQuery);
    return resourceQuery;
  }

  private createResourceQuery(order: IOrderClause[], filters: IFilterClause[],
    extraParams?: any) {
    return this.resource.all().withFilters(filters).withOrder(order).withExtraParams(extraParams);
  }

  private getCurrentOrderAndFilters(): any {
    const urlParams: ISortableParams = WindowLocationService.getHashQueryObject();
    return {
      order: Order.fromInput(urlParams.order, this.type.orderKeys) || this.type.defaultOrder,
      filters: this.getFilterClauses(urlParams, this.filterKeys, this.defaultFilters)
    };
  }

  private getFilterClauses(
    urlParams: ISortableParams,
    filterKeys: ContentFilterKeyName[],
    defaultFilters: Partial<IContentFilters<string>> = {}
  ): ILeafFilterClause[] {
    const urlParamsThatAreFilters = this.getFiltersFromUrlParams(urlParams, filterKeys);

    const filtersWithDefaultsAdded = _.defaults(urlParamsThatAreFilters, defaultFilters);
    const filterClauses = ServerFilterClauses.create(filtersWithDefaultsAdded);
    return filterClauses;
  }

  private getFiltersFromUrlParams(urlParams: ISortableParams, filterKeys: ContentFilterKeyName[]) {
    return _.pick(urlParams, filterKeys);
  }
}

export const QueryServiceInstance = new QueryService();
export type {QueryService};

